import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import ImageSidebarLayout from 'client/v3/layouts/ImageSidebarLayout';
import OptionsSidebar, { OptionsSidebarProps } from './OptionsSidebar';
import OptionsCarousel, { OptionsCarouselProps } from './OptionsCarousel';
import Loader from 'client/components/Loader';
import useConfigurationSelection from 'client/hooks/useConfigurationSelectionV2';
import { VehicleOption } from 'client/hooks/queries/useVehicleOptions';
import UIError from 'client/utils/UIError';
import url from 'client/utils/url';
import SummaryPage from 'client/v3/pages/SummaryPage';
import { SidebarRouter } from 'client/v3/Router';
import useConfig from 'client/hooks/useConfig';
import useUser from 'client/hooks/queries/useUser';
import useOrderDetail from 'client/hooks/queries/useOrderDetail';
import { OptionProps } from './OptionsSidebar/Option';
import modelCodes from 'client/constants/modelCode';
import useCurrentOption from './useCurrentOption';
import { ConfigContext } from 'client/containers/ConfigContextProvider';

type ExteriorOptions = OptionsCarouselProps['exterior'];
type InteriorOptions = OptionsCarouselProps['interior'];
type ExteriorOptionItems = OptionsSidebarProps['exterior']['items'];
type InteriorOptionItems = OptionsSidebarProps['interior']['items'];
type ProductItemPosition = OptionProps['productImagePosition']

const OPTION_CATEGORY_CODES = {
  VEH: 'VEH',
  EXC: 'EXC',
  WHL: 'WHL',
  INT: 'INT',
}

const getLimitedInteriorMessage = (
  selectedExterior: VehicleOption,
  vehicleOptions: VehicleOption[],
  selectedInterior: VehicleOption) => {
  const allInteriorOption = vehicleOptions.filter(o => o.vehicleOptionType.categoryCode === OPTION_CATEGORY_CODES.INT);

  const availableInteriorOptionIds = vehicleOptions
    .find((o) => o.vehicleOptionType.typeCode === selectedExterior.vehicleOptionType.typeCode)
    ?.combinations?.filter(
      (c) => c.vehicleOptionTypeCategoryCode === OPTION_CATEGORY_CODES.INT
    )
    ?.map((c) => c.vehicleOptionId);

  if (!availableInteriorOptionIds?.length) {
    return [];
  }

  const limitedInteriorOptions = allInteriorOption.filter(o => !availableInteriorOptionIds.includes(o.id));
  if (limitedInteriorOptions.length === 0) {
    return [];
  }

  const limitedInteriorOptionMessageForCurrentExterior =
    limitedInteriorOptions.map(
      (o) =>
        `Limited interior options available. ${o.vehicleOptionType.typeName} interior is not available in ${selectedExterior.vehicleOptionType.typeName}`
    );

  let limitedInteriorOptionMessage;
  if (limitedInteriorOptions.some(o => o.vehicleOptionType.typeCode === selectedInterior.vehicleOptionType.typeCode)) {
    limitedInteriorOptionMessage = `${selectedInterior.vehicleOptionType.typeName} interior is not available with ${selectedExterior.vehicleOptionType.typeName}`;
  }

  return [
    limitedInteriorOptionMessageForCurrentExterior,
    limitedInteriorOptionMessage
  ];
}

const getImagesByDevice = (isMobileLayout, deviceImages, defaultImages) => {
  const device = isMobileLayout ? 'mobile' : 'desktop';
  const images = deviceImages?.[device] || [];

  return images.length === 0 ? defaultImages : images;
}

export default function OptionsPage() {
  const history = useHistory();
  const qsParams = useConfig();
  const { ORDER: orderNum } = qsParams; 
  const {
    selectedModel,
    selectedVehicleOptions,
    vehicleOptions,
    modelOptions,
    configurationSelectionSummary,
    loading
  } = useConfigurationSelection();

  const {
    currentOption: centeredOption,
    handleCurrentOptionChanged: handleCenterOptionChanged
  } = useCurrentOption();

  const { user, loading: loadingUser } = useUser();
  const { order, loading: loadingOrder } = useOrderDetail(orderNum as string, {
    skip: !orderNum?.length || !user
  });

  const {
    isMobileLayout,
  } = useContext(ConfigContext);

  if (loading || loadingUser || loadingOrder) {
    return <Loader />;
  }

  if (
    !selectedModel ||
    !selectedVehicleOptions ||
    !vehicleOptions ||
    !modelOptions ||
    !configurationSelectionSummary
  ) {
    throw new UIError('Fail to get configuration selection objects');
  }

  // If it's Futurist, jump to a simple reorder page, by DDEV-6404
  if(selectedModel.fullModelCode === modelCodes.FF91Futurist.fullModelCode){
    window.location.href='/us/preorder/ff-91-Futurist/';
    return <Loader />;
  }

  const selectedExterior = selectedVehicleOptions?.find(o => o.vehicleOptionType.categoryCode === OPTION_CATEGORY_CODES.EXC) as VehicleOption;
  const selectedWheel = selectedVehicleOptions?.find(o => o.vehicleOptionType.categoryCode === OPTION_CATEGORY_CODES.WHL) as VehicleOption;
  const selectedInterior = selectedVehicleOptions?.find(o => o.vehicleOptionType.categoryCode === OPTION_CATEGORY_CODES.INT) as VehicleOption;

  if (!selectedExterior || !selectedWheel || !selectedInterior) {
    throw new UIError('Fail to find selected option in selectedVehicleOption object.');
  }

  qsParams[OPTION_CATEGORY_CODES.VEH] = selectedModel.fullModelCode;
  qsParams[OPTION_CATEGORY_CODES.EXC] = selectedExterior.vehicleOptionType.typeCode;
  qsParams[OPTION_CATEGORY_CODES.WHL] = selectedWheel.vehicleOptionType.typeCode;
  qsParams[OPTION_CATEGORY_CODES.INT] = selectedInterior.vehicleOptionType.typeCode;

  const [
    limitedInteriorOptionMessagesForExterior,
    limitedInteriorOptionMessage
  ] = getLimitedInteriorMessage(
    selectedExterior,
    vehicleOptions,
    selectedInterior
  );

  const exteriorImages: ExteriorOptions = [];
  for (const colorCode in modelOptions.EXC.color.values) {
    exteriorImages.push({
      code: colorCode,
      wheels: [
        {
          code: '_', // currently we only have one set of exterior image for all wheels.
          images: getImagesByDevice(isMobileLayout, modelOptions.EXC.color.values[colorCode].images2, modelOptions.EXC.color.values[colorCode].images)
        }
      ],
    });
  }

  // Put all wheels to subOptions of one special option and use first wheel's image set.
  const wheelImages = [
    {
      code: selectedWheel.vehicleOptionType.typeCode,
      exterior: modelOptions.WHL.values[Object.keys(modelOptions.WHL.values)[0]]
        .imageSets?.map(
          is => ({ code: is.code, images: getImagesByDevice(isMobileLayout, is.images2, is.images) })
        ),
    }
  ];

  const interiorImages: InteriorOptions = [];
  for (const code in modelOptions.INT.color.values) {
    interiorImages.push({
      code,
      images: getImagesByDevice(isMobileLayout, modelOptions.INT.color.values[code].images2, modelOptions.INT.color.values[code].images)
    });
  }

  const optionsCarouselProps = {
    currentOption: centeredOption,
    current: {
      exteriorCode: selectedExterior.vehicleOptionType.typeCode,
      wheelsCode: selectedWheel.vehicleOptionType.typeCode,
      interiorCode: selectedInterior.vehicleOptionType.typeCode,
    },
    exterior: exteriorImages,
    wheels: wheelImages,
    interior: interiorImages,
  }

  const overview = {
    modelCode: selectedModel.fullModelCode,
    model: selectedModel.name,
    edition: selectedModel.edition || '',
    name: selectedModel.name,
    productImage: modelOptions.EXC.color.values[selectedExterior.vehicleOptionType.typeCode].images[0],
    specs: selectedModel.vehicleOverviewAssets.overviewSpecs || [],
    features: selectedModel.vehicleOverviewAssets.overviewFeatures.map(of => of.title),
    price: selectedModel.basePrice,
    order: order ? {
      modelCode: order.vehicleModel.fullModelCode,
      isOnWaitlist: order.isOnWaitlist,
      ff91AllianceEligibility: order.canUpgradeEdition, // use canUpgradeEdition as ff91AllianceEligibility flag
    } : undefined,     
  }

  const exteriorOptionItems: ExteriorOptionItems = [];
  for (const code in modelOptions.EXC.color.values) {
    const value = modelOptions.EXC.color.values[code];
    const vehicleOption = vehicleOptions.find(vo => vo.vehicleOptionType.typeCode === code);
    const images = getImagesByDevice(isMobileLayout, value.images2, value.images);
    exteriorOptionItems.push({
      code,
      image: value.tileImage,
      productImage: images[1] || images[0],
      title: value.title,
      features: [], // There is no features for a option currently.
      price: vehicleOption?.price || 0,
    });
  }
  const exteriorOption = {
    code: modelOptions.EXC.categoryCode,
    name: modelOptions.EXC.categoryName,
    messages: limitedInteriorOptionMessagesForExterior,
    selectedItemCode: selectedExterior.vehicleOptionType.typeCode,
    productImageScaleRatio: 1.5,
    items: exteriorOptionItems,
  }

  const wheelSubOptions: { code: string, title: string, price: number }[] = [];
  for (const code in modelOptions.WHL.values) {
    const value = modelOptions.WHL.values[code];
    const vehicleOption = vehicleOptions.find(vo => vo.vehicleOptionType.typeCode === code);
    wheelSubOptions.push({
      code,
      // sample: 22" Forged Aluminum Alloy Wheel w/ Performance Summer Tires
      title: value.title.split('w/')[1],
      price: vehicleOption?.price || 0,
    });
  }
  const wheelImageSet = modelOptions.WHL.values[Object.keys(modelOptions.WHL.values)[0]].imageSets.find(is => is.code === selectedExterior.vehicleOptionType.typeCode); 
  const wheelOption = {
    code: modelOptions.WHL.categoryCode,
    name: modelOptions.WHL.categoryName,
    selectedItemCode: selectedWheel.vehicleOptionType.typeCode,
    productImagePosition: 'bottom' as ProductItemPosition,
    items: [
      {
        code: selectedWheel.vehicleOptionType.typeCode,
        image: modelOptions.WHL.values[Object.keys(modelOptions.WHL.values)[0]].tileImage, // use first wheel's image
        productImage: getImagesByDevice(isMobileLayout, wheelImageSet?.images2, wheelImageSet?.images)?.[0] || '',
        // sample: 22" Forged Aluminum Alloy Wheel w/ Performance Summer Tires
        title: modelOptions.WHL.values[Object.keys(modelOptions.WHL.values)[0]].title.split('w/')[0],
        features: [], // There is no features for a option currently.
        subOptions: wheelSubOptions,
      }
    ],
  }

  const interiorOptionItems: InteriorOptionItems = [];
  for (const code in modelOptions.INT.color.values) {
    const value = modelOptions.INT.color.values[code];
    const vehicleOption = vehicleOptions.find(vo => vo.vehicleOptionType.typeCode === code);
    interiorOptionItems.push({
      code,
      image: value.tileImage,
      title: value.title,
      productImage: getImagesByDevice(isMobileLayout, value.images2, value.images)[0],
      features: value.specs?.map(spec => `${spec.title}: ${spec.description}`) || [],
      price:vehicleOption?.price || 0, 
    });
  }
  const interiorOption = {
    code: modelOptions.INT.categoryCode,
    name: modelOptions.INT.categoryName,
    selectedItemCode: selectedInterior.vehicleOptionType.typeCode,
    items: interiorOptionItems,
    messages: limitedInteriorOptionMessage ? [limitedInteriorOptionMessage]: [],
  }

  const summary = {
    modelCode: selectedModel.fullModelCode,
    basePrice: configurationSelectionSummary.basePrice,
    totalPrice: configurationSelectionSummary.subtotal,
    summaryPageUrl: url.getPageNavigationV3('summary', qsParams),
    disableButton: !!limitedInteriorOptionMessage,
    exterior: {
      code: selectedExterior.vehicleOptionType.typeCode,
      name: selectedExterior.vehicleOptionType.typeName,
      price: selectedExterior.price,
    },
    wheels: {
      code: selectedWheel.vehicleOptionType.typeCode,
      name: selectedWheel.vehicleOptionType.typeName,
      price: selectedWheel.price,
    },
    interior: {
      code: selectedInterior.vehicleOptionType.typeCode,
      name: selectedInterior.vehicleOptionType.typeName,
      price: selectedInterior.price,
    },
  }

  const handleOptionsChanged = (optionCode: string, selectedOptionItemCode: string) => {
    qsParams[optionCode] = selectedOptionItemCode;
    const newUrl = url.getPageNavigationV3('options', qsParams);
    history.replace(newUrl);
  }

  const allowGotoModelSelection =
    !!order &&
    ((order.canDowngradeEdition &&
      order.vehicleModel.fullModelCode ===
        modelCodes.FF91FuturistAlliance.fullModelCode) ||
      (order.canUpgradeEdition &&
        order.vehicleModel.fullModelCode ===
          modelCodes.FF91Futurist.fullModelCode));

  const optionsSidebarProps = {
    modelCode: selectedModel.fullModelCode,
    allowGotoModelSelection,
    overview,
    exterior: exteriorOption,
    wheels: wheelOption,
    interior: interiorOption,
    summary,
    onOptionsChanged: handleOptionsChanged,
    onCenterOptionChanged: handleCenterOptionChanged,
  }

  return (
    <ImageSidebarLayout
      image={<OptionsCarousel {...optionsCarouselProps} />}
      sidebar={
        <SidebarRouter
          summarySidebar={SummaryPage}
        >
          {/* Put OptionsSidebar in child node to avoid the options sidebar is re-created when the page is rendered */}
          <OptionsSidebar {...optionsSidebarProps} /> 
        </SidebarRouter>
      }
    />
  );
}
